import { useMemo, useState } from 'react';
import { IconAlertTriangle, IconCheck, IconChevronLeft, IconPhone, IconPlus, IconPrinter, IconSearch, IconSend, IconX } from '@tabler/icons-react';
import { useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { norm } from '../finance/view';
import type { ArCustomer } from '../finance/types';
import { arChipFilter, arObh, arRows, arSummary, type ArChip, type ArRow } from './ar';
import { DEB_ROWS } from './deb';
import { RCP_ROWS, rcpOpen } from './rcp';
import { nf, ty } from './ArCards';
import s from './arm.module.css';
import './ar.i18n';
import './arphone.i18n';

// Mint-AR-Phone (390): khung 1 = danh sách thẻ khách (chip · tìm · tổng) · khung 2 = chi tiết khách (tổng quan · tuổi nợ · debit mở · thu nợ). TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.
type PChip = ArChip | 'rcp';
const AGC = ['var(--d-blue)', 'var(--d-amber)', 'var(--d-amber)', 'var(--err)', 'var(--err)']; // dải tuổi nợ: chưa hạn → > 90
const AGK = ['ap.ag0', '1–30', '31–60', '61–90', '> 90']; // khoá i18n hoặc nhãn số

export function ArPhone() {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<PChip>('over');
  const [open, setOpen] = useState<string | null>(null);
  const noWrite = () => toast({ msg: t('ar.noWrite') });
  const money = access.money;
  const mask = (x: string) => (money ? x : '•••••');

  const src = (useList('finArCustomer', { limit: 500 }).data?.items ?? []) as readonly ArCustomer[];
  const all = useMemo(() => arRows(src).sort((a, b) => b.over - a.over), [src]);
  const rcpPending = RCP_ROWS.filter(rcpOpen).length;
  const rows = useMemo(() => {
    const n = norm(q.trim());
    const base = chip === 'rcp' ? all.filter((r) => RCP_ROWS.some((x) => rcpOpen(x) && x.cus === r.customer)) : arChipFilter(all, chip);
    return base.filter((r) => !n || norm(`${r.customer} ${r.sale}`).includes(n));
  }, [all, chip, q]);
  const sum = arSummary(all);
  const cur = open ? all.find((r) => r.customer === open) : undefined;

  const chips: readonly (readonly [PChip, string, number])[] = [
    ['over', t('ap.cOver'), arChipFilter(all, 'over').length], ['hold', t('ap.cHold'), arChipFilter(all, 'hold').length], ['mine', t('ap.cMine'), arChipFilter(all, 'mine').length],
    ['rcp', t('ap.cRcp'), rcpPending], ['obh', t('ap.cObh'), arChipFilter(all, 'obh').length], ['all', t('ap.cAll'), all.length],
  ];

  if (cur && state === 'ready') return <Detail r={cur} money={money} onBack={() => setOpen(null)} noWrite={noWrite} />;

  return (
    <Page kind="table">
      <PageHeader title={t('arph.title')} findHint={false} />
      <div className={`${s.page} ${s.phone}`}>
        <StateView state={state} skeleton={<Skeleton rows={6} />}>
          <label className={s.search}>
            <IconSearch size={17} aria-hidden="true" />
            <input aria-label={t('ap.findAria')} placeholder={t('ap.findPh')} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className={s.chips}>
            {chips.map(([k, l, n]) => <button key={k} type="button" aria-pressed={chip === k} onClick={() => setChip(k)}>{l} <span>{n}</span></button>)}
          </div>
          <div className={s.kpis}>
            <div className={s.kpi}><div>{t('ap.rec')}</div><b>{mask(ty(sum.total))}</b></div>
            <div className={s.kpi}><div>{t('ap.over')}</div><b className={s.err}>{mask(ty(sum.over))}</b></div>
          </div>
          {rows.length === 0 ? <StateView state="empty" title={t('ap.emptyT')} desc={t('ap.emptyD')} /> : rows.map((r) => <Card key={r.customer} r={r} money={money} onOpen={() => setOpen(r.customer)} onRemind={noWrite} />)}
        </StateView>
      </div>
    </Page>
  );
}

function Card({ r, money, onOpen, onRemind }: { r: ArRow; money: boolean; onOpen: () => void; onRemind: () => void }) {
  const t = useT();
  const m = (x: string) => (money ? x : '•••••');
  const o = arObh(r.customer);
  const vals = [r.current, r.d1_30, r.d31_60, r.d61_90, r.d90];
  return (
    <div className={s.wrap}>
      <button type="button" className={s.pcard} onClick={onOpen}>
        <span className={s.top}><b>{r.customer}</b><b>{m(`${nf(r.total)} tr`)}</b></span>
        {(o.open > 0 || r.over > 0) && (
          <span className={s.tags}>
            {o.open > 0 && <span className={`${s.tag} ${o.red ? s.te : s.tw}`}>{t('ap.obh', { n: nf(o.open) })}</span>}
            {r.over > 0 && <span className={s.od} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><IconAlertTriangle size={13} aria-hidden="true" />{t('ap.overLc', { n: m(nf(r.over)) })}</span>}
          </span>
        )}
        <span className={s.bar} aria-hidden="true">{vals.map((v, i) => v > 0 && <i key={i} style={{ flex: v, background: AGC[i] }} />)}</span>
        <span className={s.pbot}>
          <span><i className={s.av} aria-hidden="true">{r.sale.slice(0, 2)}</i>{r.sale} · DSO {money ? r.dso : '•••'}</span>
          {r.flag && <span className={`${s.tag} ${r.block ? s.te : s.tw}`}>{r.flag}</span>}
        </span>
      </button>
      {r.over > 0 && <button type="button" className={s.swipe} aria-label={t('ap.remindAria', { c: r.customer })} onClick={onRemind}><IconSend size={16} aria-hidden="true" />{t('ap.remind')}</button>}
    </div>
  );
}

function Detail({ r, money, onBack, noWrite }: { r: ArRow; money: boolean; onBack: () => void; noWrite: () => void }) {
  const t = useT();
  const m = (x: string) => (money ? x : '•••••');
  const left = r.limit - r.total;
  const use = Math.min(1, r.total / r.limit);
  const vals = [r.current, r.d1_30, r.d31_60, r.d61_90, r.d90];
  const debits = DEB_ROWS.filter((d) => d.bill === r.customer && d.left !== 0);
  const o = arObh(r.customer);
  const pend = r.flag === 'Giữ DO';
  return (
    <Page kind="table">
      <PageHeader
        title={r.customer}
        findHint={false}
        actions={
          <>
            <button type="button" className={s.back} aria-label={t('ap.back')} onClick={onBack}><IconChevronLeft size={22} aria-hidden="true" /></button>
            <a className={s.call} aria-label={t('ap.call')} href="tel:" onClick={(e) => { e.preventDefault(); noWrite(); }}><IconPhone size={19} aria-hidden="true" /></a>
          </>
        }
      />
      <div className={`${s.page} ${s.phone}`}>
        <section className={s.sec} aria-label={t('ap.ov')}>
          <div className={s.pills}>
            {r.flag && <span className={`${s.tag} ${r.block ? s.te : s.tw}`}>{r.flag}</span>}
            {o.open > 0 && <span className={`${s.tag} ${o.red ? s.te : s.tw}`}>{t('ap.obh', { n: nf(o.open) })}</span>}
          </div>
          <div className={s.sh}><b>{t('ap.ov')}</b><span>{t('ap.ovUnit')}</span></div>
          <div className={s.grid2}>
            <div className={s.gk}><div>{t('ap.total')}</div><b>{m(nf(r.total))}</b></div>
            <div className={s.gk}><div>{t('ap.over')}</div><b className={r.over ? s.err : ''}>{m(nf(r.over))}</b></div>
            <div className={s.gk}><div>{t('ap.inTerm')}</div><b>{m(nf(r.current))}</b></div>
            <div className={s.gk}><div>{t('ap.dso')}</div><b className={r.dso > 45 ? s.err : ''}>{m(String(r.dso))}</b></div>
            <div className={`${s.gk} ${s.wide}`}><div>{t('ap.limLeft')}</div><b>{m(left >= 0 ? nf(left) : t('ap.exceed', { n: nf(-left) }))}</b><div className={s.bar} style={{ marginTop: 8 }}><i style={{ width: `${Math.round(use * 100)}%`, background: left < 0 ? 'var(--err)' : use > 0.85 ? 'var(--warn)' : 'var(--d-blue)' }} /></div></div>
          </div>
        </section>
        <section className={s.sec} aria-label={t('ap.aging')}>
          <div className={s.sh}><b>{t('ap.aging')}</b><span>{t('ap.agingUnit')}</span></div>
          <div className={s.agbar} role="img" aria-label={t('ap.agingAria')}>{vals.map((v, i) => <i key={i} style={{ flex: Math.max(v, 0.001), background: AGC[i] }} />)}</div>
          <div className={s.agl}>{vals.map((v, i) => <div key={i}>{AGK[i]!.startsWith('ap.') ? t(AGK[i]!) : AGK[i]}<b>{m(v ? nf(v) : '—')}</b></div>)}</div>
        </section>
        {pend && (
          <section className={s.appr} aria-label={t('ap.apprAria')}>
            <div className={s.sh}><b>{t('ap.apprT', { no: 'SI260600980' })}</b><span>{t('ap.apprWho')}</span></div>
            <div className={s.btns}>
              <button type="button" className={`${s.act} ${s.pri}`} onClick={noWrite}><IconCheck size={16} aria-hidden="true" />{t('arph.approve')}</button>
              <button type="button" className={s.act} style={{ flex: '0 0 auto' }} onClick={noWrite}><IconX size={16} aria-hidden="true" />{t('arph.reject')}</button>
            </div>
          </section>
        )}
        <section className={s.sec} aria-label={t('ap.debOpen')}>
          <div className={s.sh}><b>{t('ap.debN', { n: debits.length })}</b><span>{t('ap.unit')}</span></div>
          <div role="table" aria-label={t('ap.debOpen')}>
            <div role="row" className={`${s.trow} ${s.h}`}><span role="columnheader">{t('ap.hCode')}</span><span role="columnheader">{t('ap.hDue')}</span><span role="columnheader">{t('ap.hOver')}</span><span role="columnheader">{t('arph.hLeft')}</span></div>
            {debits.map((d) => (
              <div role="row" key={d.no} className={s.trow}>
                <span role="cell"><b>{d.no}</b><small>{d.job}</small></span><span role="cell">{d.due.replace(/^Hạn /, '')}</span><span role="cell" className={d.overdue ? s.err : ''}>{d.overdue ? t('ap.lateShort') : '—'}</span><span role="cell">{m(nf(d.left))}</span>
              </div>
            ))}
          </div>
        </section>
        <button type="button" className={s.act} onClick={noWrite}><IconPlus size={16} aria-hidden="true" />{t('ap.pay')}</button>
        <div className={s.btns}>
          <button type="button" className={s.act} onClick={noWrite}><IconPrinter size={16} aria-hidden="true" />{t('ap.soaPrint')}</button>
          <button type="button" className={`${s.act} ${s.soft}`} onClick={noWrite}><IconSend size={16} aria-hidden="true" />{t('ap.soaSend')}</button>
        </div>
        {r.over > 0 && <button type="button" className={s.fab} onClick={noWrite}>{t('ap.remind')}</button>}
      </div>
    </Page>
  );
}
